import {
  ConcurrentSessionsBehaviorSchema,
  LoginBehaviorSourceSchema,
  LogoutScopeBehaviorSchema,
  TokenRotationBehaviorSchema,
} from '@terra/shared-schemas-orchestrator';
import { z } from 'zod';

export const MAX_PARALLEL_LOGINS_LIMIT = 100;
export const DEFAULT_MAX_PARALLEL_LOGINS = 8;

const detectedLoginBehaviorFieldSchema = <
  const Values extends readonly [string, ...string[]],
>(
  values: Values,
) =>
  z.discriminatedUnion('state', [
    z.object({
      state: z.literal('detected'),
      value: z.enum(values),
    }),
    z.object({
      state: z.literal('mixed'),
      value: z.null(),
    }),
    z.object({
      state: z.literal('none'),
      value: z.null(),
    }),
  ]);

const undetectedField = () => ({ state: 'none' as const, value: null });

export const UNDETECTED_LOGIN_BEHAVIOR = {
  concurrentSessions: undetectedField(),
  logoutScope: undetectedField(),
  tokenRotation: undetectedField(),
};

export const DetectedLoginBehaviorSchema = z.object({
  concurrentSessions: detectedLoginBehaviorFieldSchema([
    'ALLOWED',
    'ONE_PER_ACCOUNT',
  ]),
  logoutScope: detectedLoginBehaviorFieldSchema([
    'THIS_SESSION',
    'ALL_SESSIONS',
  ]),
  tokenRotation: detectedLoginBehaviorFieldSchema(['ROTATES', 'STABLE']),
});

export type DetectedLoginBehavior = z.infer<typeof DetectedLoginBehaviorSchema>;

const MaxParallelLoginsValueSchema = z
  .int()
  .min(1)
  .max(MAX_PARALLEL_LOGINS_LIMIT);

const loginBehaviorSettingSchema = <Value extends z.ZodType>(value: Value) =>
  z.object({
    value,
    source: LoginBehaviorSourceSchema,
    updatedAt: z.coerce.date(),
  });

export const LoginBehaviorProfileSchema = z.object({
  concurrentSessions: loginBehaviorSettingSchema(
    ConcurrentSessionsBehaviorSchema,
  ).nullish(),
  logoutScope: loginBehaviorSettingSchema(LogoutScopeBehaviorSchema).nullish(),
  tokenRotation: loginBehaviorSettingSchema(
    TokenRotationBehaviorSchema,
  ).nullish(),
  maxParallelLogins: loginBehaviorSettingSchema(
    MaxParallelLoginsValueSchema,
  ).nullish(),
});

export const LoginBehaviorUpdateSchema = z.object({
  concurrentSessions: ConcurrentSessionsBehaviorSchema.optional(),
  logoutScope: LogoutScopeBehaviorSchema.optional(),
  tokenRotation: TokenRotationBehaviorSchema.optional(),
  maxParallelLogins: MaxParallelLoginsValueSchema.nullable().optional(),
});

export type LoginBehaviorProfile = z.infer<typeof LoginBehaviorProfileSchema>;
export type LoginBehaviorUpdate = z.infer<typeof LoginBehaviorUpdateSchema>;
export type LoginBehaviorField = keyof LoginBehaviorUpdate;
